import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { useDebounce } from './useDebounce'
import {
  listImages,
  getImage,
  updateImage,
  deleteImage,
  uploadSingleImage,
  getBulkImageUploadUrls,
  uploadToPresignedUrl,
  confirmImageUpload,
  listLineItemImages,
  assignImagesToLineItem,
  unassignImageFromLineItem,
  setDefaultLineItemImage,
  reorderLineItemImage,
} from '@/api/images'
import { queryKeys } from '@/lib/queryKeys'
import type {
  PaginatedImagesResponse,
  ImageRead,
  ImagePatchRequest,
  LineItemImageRead,
  ReorderImageRequest,
} from '@/types/image'
import { toast } from '@/stores/useToastStore'

// ─── Queries ────────────────────────────────────────────────────

interface UseImagesListParams {
  search: string
  page: number
  pageSize: number
}

export function useImagesList({ search, page, pageSize }: UseImagesListParams) {
  const debouncedSearch = useDebounce(search, 300)

  const filters = {
    search: debouncedSearch,
    page,
    pageSize,
  }

  return useQuery<PaginatedImagesResponse>({
    queryKey: queryKeys.images.list(filters),
    queryFn: () =>
      listImages({
        search: debouncedSearch || undefined,
        skip: (page - 1) * pageSize,
        limit: pageSize,
      }),
  })
}

const GALLERY_PAGE_SIZE = 30

export function useInfiniteImagesList(search: string) {
  const debouncedSearch = useDebounce(search, 300)

  return useInfiniteQuery<PaginatedImagesResponse>({
    queryKey: queryKeys.images.list({
      search: debouncedSearch,
      infinite: true,
    }),
    queryFn: ({ pageParam }) =>
      listImages({
        search: debouncedSearch || undefined,
        skip: pageParam as number,
        limit: GALLERY_PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: lastPage =>
      lastPage.pagination.has_more
        ? lastPage.pagination.skip + lastPage.pagination.limit
        : undefined,
  })
}

export function useImage(imageId: string | undefined) {
  return useQuery<ImageRead>({
    queryKey: queryKeys.images.detail(imageId ?? ''),
    queryFn: () => getImage(imageId!),
    enabled: !!imageId,
  })
}

export function useLineItemImages(
  planId: string | undefined,
  versionId: string | undefined,
  lineId: string | undefined
) {
  return useQuery<LineItemImageRead[]>({
    queryKey: queryKeys.lineItemImages.byLine(
      planId ?? '',
      versionId ?? '',
      lineId ?? ''
    ),
    queryFn: () => listLineItemImages(planId!, versionId!, lineId!),
    enabled: !!planId && !!versionId && !!lineId,
  })
}

// ─── Mutations ──────────────────────────────────────────────────

export function useUpdateImage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      imageId,
      data,
    }: {
      imageId: string
      data: ImagePatchRequest
    }) => updateImage(imageId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.images.all })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update image')
    },
  })
}

export function useDeleteImage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (imageId: string) => deleteImage(imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.images.all })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete image')
    },
  })
}

export function useUploadImage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      file,
      name,
      description,
    }: {
      file: File
      name: string
      description?: string | null
    }) => uploadSingleImage(file, name, description),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.images.all })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to upload image')
    },
  })
}

export interface BulkUploadItem {
  file: File
  name: string
  description?: string | null
}

const UPLOAD_CONCURRENCY = 10

async function runWithConcurrency<T>(
  tasks: (() => Promise<T>)[],
  concurrency: number
): Promise<T[]> {
  const results: T[] = new Array(tasks.length)
  let index = 0

  async function worker() {
    while (index < tasks.length) {
      const i = index++
      results[i] = await tasks[i]()
    }
  }

  await Promise.all(Array.from({ length: concurrency }, () => worker()))
  return results
}

export function useBulkUploadImages() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (items: BulkUploadItem[]): Promise<ImageRead[]> => {
      // Step 1: Get bulk presigned URLs
      const { uploads } = await getBulkImageUploadUrls({
        images: items.map(item => ({
          original_filename: item.file.name,
          content_type: item.file.type || 'image/jpeg',
          name: item.name,
          description: item.description,
        })),
      })

      // Step 2: Upload each file to its presigned URL (concurrency-limited)
      await runWithConcurrency(
        uploads.map(
          (upload, i) => () =>
            uploadToPresignedUrl(upload.upload_url, items[i].file)
        ),
        UPLOAD_CONCURRENCY
      )

      // Step 3: Confirm each upload (concurrency-limited)
      const confirmed = await runWithConcurrency(
        uploads.map(
          (upload, i) => () =>
            confirmImageUpload(upload.image_id, items[i].file.size)
        ),
        UPLOAD_CONCURRENCY
      )

      return confirmed
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.images.all })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to upload images')
    },
  })
}

// ─── Line Item Image Mutations ──────────────────────────────────

export function useAssignImagesToLine(
  planId: string,
  versionId: string,
  lineId: string
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (imageIds: string[]) =>
      assignImagesToLineItem(planId, versionId, lineId, imageIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.lineItemImages.byLine(planId, versionId, lineId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
      })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to assign images')
    },
  })
}

export function useUnassignImageFromLine(
  planId: string,
  versionId: string,
  lineId: string
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (imageId: string) =>
      unassignImageFromLineItem(planId, versionId, lineId, imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.lineItemImages.byLine(planId, versionId, lineId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
      })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to remove image')
    },
  })
}

export function useSetDefaultLineImage(
  planId: string,
  versionId: string,
  lineId: string
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (imageId: string) =>
      setDefaultLineItemImage(planId, versionId, lineId, imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.lineItemImages.byLine(planId, versionId, lineId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
      })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to set default image')
    },
  })
}

export function useReorderLineItemImage(
  planId: string,
  versionId: string,
  lineId: string
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      imageId,
      data,
    }: {
      imageId: string
      data: ReorderImageRequest
    }) => reorderLineItemImage(planId, versionId, lineId, imageId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.lineItemImages.byLine(planId, versionId, lineId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
      })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to reorder image')
    },
  })
}
